import { useRef, useEffect } from 'react';

const UseScrollHandlers = (companyId) => {
  // Refs for scroll state
  const touchStartPos = useRef({});
  const isDragging = useRef({});
  const lastClientX = useRef({});
  const scrollLeft = useRef({});
  const momentum = useRef({});
  const animationFrame = useRef(null);
  const lastTimestamp = useRef({});
  const velocityTracker = useRef({});

  // Clean up inertia animation on unmount
  useEffect(() => {
    return () => {
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
    };
  }, []);

  // Apply momentum scroll effect
  const applyMomentum = (scrollContainer) => {
    if (!momentum.current[companyId] || !scrollContainer) return;
    
    const runMomentumScroll = () => {
      // End condition relaxed for longer slide
      if (Math.abs(momentum.current[companyId]) < 0.15) {
        momentum.current[companyId] = 0;
        return;
      }
      
      scrollContainer.scrollLeft += momentum.current[companyId];
      // Decay coefficient adjusted
      momentum.current[companyId] *= 0.92;
      
      animationFrame.current = requestAnimationFrame(runMomentumScroll);
    };
    
    runMomentumScroll();
  };

  // Touch start handler
  const handleTouchStart = (e, scrollContainer) => {
    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
      momentum.current[companyId] = 0;
    }
    
    if (!scrollContainer) return;
    
    // Record drag start state
    isDragging.current[companyId] = true;
    
    // Record initial position
    touchStartPos.current[companyId] = e.touches[0].clientX;
    lastClientX.current[companyId] = e.touches[0].clientX;
    scrollLeft.current[companyId] = scrollContainer.scrollLeft;
    
    // Initialize velocity tracking
    momentum.current[companyId] = 0;
    velocityTracker.current[companyId] = [];
    lastTimestamp.current[companyId] = Date.now();
    
    // Add dragging class
    scrollContainer.classList.add('dragging');
  };

  // Touch move handler
  const handleTouchMove = (e, scrollContainer) => {
    if (!isDragging.current[companyId] || !scrollContainer) return;
    
    // Prevent default scroll behavior
    e.preventDefault();
    
    const currentX = e.touches[0].clientX;
    const deltaX = lastClientX.current[companyId] - currentX;
    const timestamp = Date.now();
    const timeElapsed = timestamp - lastTimestamp.current[companyId];
    
    // Velocity tracking - record past movements for averaging
    if (timeElapsed > 0) {
      const velocity = deltaX / timeElapsed * 15; // Apply coefficient to adjust sensitivity
      velocityTracker.current[companyId].push(velocity);
      
      // Keep only the latest 5 samples
      if (velocityTracker.current[companyId].length > 5) {
        velocityTracker.current[companyId].shift();
      }
    }
    
    // Update scroll position - coefficient adjusted to control responsiveness
    scrollContainer.scrollLeft += deltaX * 1.0;
    
    // Save current position and time for next calculation
    lastClientX.current[companyId] = currentX;
    lastTimestamp.current[companyId] = timestamp;
  };

  // Touch end handler
  const handleTouchEnd = (scrollContainer) => {
    if (!scrollContainer) return;
    
    isDragging.current[companyId] = false;
    
    // Calculate average velocity from recent samples
    if (velocityTracker.current[companyId] && velocityTracker.current[companyId].length > 0) {
      const recentVelocities = velocityTracker.current[companyId].slice(-3); // Use latest 3 samples
      const avgVelocity = recentVelocities.reduce((a, b) => a + b, 0) / recentVelocities.length;
      
      // Set initial momentum from average velocity
      momentum.current[companyId] = avgVelocity * 4; // Apply coefficient to adjust initial momentum
    }
    
    // Remove dragging class
    scrollContainer.classList.remove('dragging');
    
    // Apply momentum scrolling
    applyMomentum(scrollContainer);
  };

  // Mouse down handler
  const handleMouseDown = (e, scrollContainer) => {
    // Only process primary button (left click)
    if (e.button !== 0) return;
    
    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
      momentum.current[companyId] = 0;
    }
    
    if (!scrollContainer) return;
    
    // Record drag start
    isDragging.current[companyId] = true;
    
    // Record initial position
    touchStartPos.current[companyId] = e.clientX;
    lastClientX.current[companyId] = e.clientX;
    scrollLeft.current[companyId] = scrollContainer.scrollLeft;
    
    // Initialize velocity tracking
    velocityTracker.current[companyId] = [];
    lastTimestamp.current[companyId] = Date.now();
    
    // Change cursor style
    scrollContainer.style.cursor = 'grabbing';
    
    // Add dragging class
    scrollContainer.classList.add('dragging');
    
    // Prevent default drag operation
    e.preventDefault();
  };

  // Mouse move handler
  const handleMouseMove = (e, scrollContainer) => {
    if (!scrollContainer || !isDragging.current[companyId]) return;
    
    // Prevent default drag operation
    e.preventDefault();
    
    // Calculate distance moved
    const currentX = e.clientX;
    const deltaX = lastClientX.current[companyId] - currentX;
    const timestamp = Date.now();
    const timeElapsed = timestamp - lastTimestamp.current[companyId];
    
    // Velocity tracking
    if (timeElapsed > 0) {
      const velocity = deltaX / timeElapsed * 8;
      velocityTracker.current[companyId].push(velocity);
      
      // Keep only the latest 5 samples
      if (velocityTracker.current[companyId].length > 5) {
        velocityTracker.current[companyId].shift();
      }
    }
    
    // Update scroll position - higher coefficient for better responsiveness
    scrollContainer.scrollLeft += deltaX * 2.5; // Adjusted coefficient
    
    // Update reference point
    lastClientX.current[companyId] = currentX;
    lastTimestamp.current[companyId] = timestamp;
  };

  // Mouse up handler
  const handleMouseUp = (scrollContainer) => {
    if (!isDragging.current[companyId] || !scrollContainer) return;
    
    // End dragging
    isDragging.current[companyId] = false;
    
    // Calculate average velocity from recent samples
    if (velocityTracker.current[companyId] && velocityTracker.current[companyId].length > 0) {
      const recentVelocities = velocityTracker.current[companyId].slice(-3); // Use latest 3 samples
      const avgVelocity = recentVelocities.reduce((a, b) => a + b, 0) / recentVelocities.length;
      
      // Set initial momentum from average velocity
      momentum.current[companyId] = avgVelocity * 8; // Adjusted coefficient
    }
    
    // Reset cursor style
    scrollContainer.style.cursor = 'grab';
    
    // Remove dragging class
    scrollContainer.classList.remove('dragging');
    
    // Apply momentum scrolling
    applyMomentum(scrollContainer);
  };

  // Mouse leave handler (when cursor exits container)
  const handleMouseLeave = (scrollContainer) => {
    // End drag process if dragging
    if (isDragging.current[companyId]) {
      handleMouseUp(scrollContainer);
    }
  };

  return {
    isDragging: isDragging.current,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave
  };
};

export default UseScrollHandlers;